import { IPromise, IHttpService, IQService } from "angular";

import { IADRoleMapping, IADConnection, IConfigActivity } from "./models";
import { ICollection } from "../models";
import { IAppConfig, EnvironmentService } from "@gtmhub/env";
import { storage } from "@gtmhub/core/storage";
import { IAuth0AuthConfig } from "@gtmhub/env";
import { SSOIntegration } from "@gtmhub/configuration";

export class IntegrationService {
  public static $inject = ["appConfig", "$http", "$q", "EnvironmentService"];

  constructor(private appConfig: IAppConfig, private $http: IHttpService, private $q: IQService, private env: EnvironmentService) {}

  public deleteConfig = (section: string) => {
    const url = this.env.getIntegrationsEndpoint("/config/" + section);
    const deferred = this.$q.defer();

    this.$http.delete(url).then(() => {
      deferred.resolve();
    });

    return deferred.promise;
  };

  public createConfig = (config: any, section: any) => {
    const url = this.env.getIntegrationsEndpoint("/config/" + section);
    const deferred = this.$q.defer();

    this.$http.put(url, config).then((result) => {
      deferred.resolve(result.data);
    });

    return deferred.promise;
  };

  public updateConfig = (config: any, section: any) => {
    const url = this.env.getIntegrationsEndpoint("/config/" + section + "/activities");
    const deferred = this.$q.defer();

    this.$http.patch(url, config).then((result) => {
      deferred.resolve(result.data);
    });

    return deferred.promise;
  };

  public getSlackButton = (): IPromise<string> => {
    const url = this.env.getIntegrationsEndpoint("/slack/button");

    const deferred = this.$q.defer<string>();

    this.$http.get(url).then(
      (result) => {
        deferred.resolve(result.data as string);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  };

  public getConfig = (integration: string): IPromise<any> => {
    const url = this.env.getIntegrationsEndpoint("/config/" + integration);
    const deferred = this.$q.defer();

    this.$http.get(url).then(
      (collection) => {
        deferred.resolve(collection.data);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  };

  // When working with Auth0 Connections, Auth0 returns us URLs with the "auth0.com" domain.
  // Which means that the user must follow a domain that is different than gtmhub.com.
  // We already have the "auth.gtmhub.com" domain configured in Auth0, so we want to use that domain.
  // Auth0 doesn't allow us to retreive our custom domain from their Connections API, thus we're doing the dirty work here.
  private replaceIntegrationUrlWithCustomUrl = (data: IADConnection): void => {
    if (data) {
      const auth0 = this.appConfig.auth as IAuth0AuthConfig;
      const dcConfig = auth0.dc[this.appConfig.env.dc];

      const modifiedURL = new URL(data.provisioning_ticket_url);
      modifiedURL.hostname = dcConfig.domain;
      data.provisioning_ticket_url = modifiedURL.toString();
    }
  };

  public getIntegrations = () => {
    const accountName = storage.get("accountName");
    const url = this.env.getIntegrationsEndpoint("/auth0/" + accountName + "/connections");
    const deferred = this.$q.defer();

    this.$http.get(url).then(
      (thing) => {
        if (thing.data) {
          this.replaceIntegrationUrlWithCustomUrl(thing.data[0] as IADConnection);
        }
        deferred.resolve(thing.data);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  };

  public getMappings(): IPromise<IADRoleMapping[]> {
    const url = this.env.getApiEndpoint("/roles/account/mapping");

    const deferred = this.$q.defer<IADRoleMapping[]>();

    this.$http.get<IADRoleMapping[]>(url).then(
      (result) => {
        deferred.resolve(result.data);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  }

  public addADConnection(adConnection: IADConnection): IPromise<IADConnection> {
    const accountName = storage.get("accountName");
    const url = this.env.getIntegrationsEndpoint("/auth0/" + accountName + "/connections");
    const deferred = this.$q.defer<IADConnection>();

    this.$http.post<IADConnection>(url, adConnection).then(
      (result) => {
        this.replaceIntegrationUrlWithCustomUrl(result.data);
        deferred.resolve(result.data);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  }

  public updateProfilePictureEditingPermission(data: Pick<SSOIntegration, "id" | "canEditPicture">): IPromise<IADConnection> {
    const url = this.env.getIntegrationsEndpoint("/auth0/connections/" + data.id);
    const deferred = this.$q.defer<IADConnection>();
    const adConnection = {
      canEditPicture: data.canEditPicture,
    };

    this.$http.patch<IADConnection>(url, adConnection).then(
      (result) => {
        this.replaceIntegrationUrlWithCustomUrl(result.data);
        deferred.resolve(result.data);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  }

  public updateADRoleMapping(mapping: IADRoleMapping[]): IPromise<IADRoleMapping[]> {
    const url = this.env.getApiEndpoint("/roles/account/mapping");
    const deferred = this.$q.defer<IADRoleMapping[]>();

    this.$http.post<IADRoleMapping[]>(url, mapping).then(
      (result) => {
        deferred.resolve(result.data);
      },
      (response) => {
        deferred.reject(response);
      }
    );

    return deferred.promise;
  }

  public deleteADConnection(): IPromise<boolean> {
    const accountName = storage.get("accountName");
    const url = this.env.getIntegrationsEndpoint("/auth0/" + accountName + "/connections");
    const deferred = this.$q.defer<boolean>();

    this.$http.delete<boolean>(url).then(() => {
      deferred.resolve();
    });

    return deferred.promise;
  }

  public deleteMapping(id): IPromise<boolean> {
    const url = this.env.getApiEndpoint("/roles/account/mapping/" + id);
    const deferred = this.$q.defer<boolean>();

    this.$http.delete<boolean>(url).then(() => {
      deferred.resolve();
    });

    return deferred.promise;
  }

  public getConfigActivities(): IPromise<ICollection<IConfigActivity>> {
    const url = this.env.getIntegrationsEndpoint("/config/all-activities");
    const deferred = this.$q.defer<ICollection<IConfigActivity>>();

    this.$http.get<ICollection<IConfigActivity>>(url).then((collection) => {
      deferred.resolve(collection.data);
    });

    return deferred.promise;
  }
}
